import { Map } from 'immutable';

import { getTotalStudentNumbers } from '®shared/reading-list/hierarchy/common';
import {
  NodesEditorActionTypes,
  NodesEditorActions,
} from '®shared/reading-list/hierarchy/editor';
import {
  ListDetailsEditorActionTypes,
  ListDetailsEditorActions,
} from '®shared/reading-list/list-details/editor/list-details-editor.actions';
import {
  ListOwnersEditorActionTypes,
  ListOwnersEditorActions,
} from '®shared/reading-list/list-owners';
import {
  EditableList,
  ListPublishingState,
  ReadingListDetails,
  convertReadingListToEditableList,
} from '®shared/reading-list/models';
import {
  RequestReviewActionTypes,
  RequestReviewSucceededAction,
} from '®shared/reviews/request-review';

import { QueueActionTypes, QueueActions } from '../edit/queue/queue.actions';

import { ContainerEditActionTypes } from './container-edit.actions';
import {
  ContainerPersistEditActionTypes,
  PersistPublishListAction,
  PersistPublishListCheckAction,
  PersistSuccessAction,
} from './container-persist-edit.actions';
import { ContainerActionTypes, ContainerActions } from './container.actions';

export type ListByIdActions =
  | ContainerActions
  | NodesEditorActions
  | ListDetailsEditorActions
  | ListOwnersEditorActions
  | PersistSuccessAction
  | PersistPublishListAction
  | PersistPublishListCheckAction
  | RequestReviewSucceededAction
  | QueueActions;

export function listsByIdReducer(
  state: Map<string, EditableList>,
  action: ListByIdActions,
): Map<string, EditableList> {
  switch (action.type) {
    case ContainerActionTypes.CONTAINER_INITIALISED: {
      if (action.payload.container instanceof ReadingListDetails) {
        const editableList: EditableList = convertReadingListToEditableList(
          action.payload.container,
        );
        return state.set(editableList.id, editableList);
      }
      return state;
    }

    case QueueActionTypes.UNLOCK_QUEUE_AND_SHIFT: {
      const editAction = action.payload.persistAction.editAction;
      const lastPersistActionWasNotAPublishListAction =
        editAction.type !== ContainerEditActionTypes.PUBLISH_LIST &&
        editAction.type !== ContainerEditActionTypes.RESUME_PUBLISH_LIST;

      const listId = editAction.payload.listId;
      const publishingState = lastPersistActionWasNotAPublishListAction
        ? ListPublishingState.NO_PUBLISH_ACTIONED
        : ListPublishingState.PUBLISH_ACTION_SUCCEEDED;
      const hasBeenPublishedBefore = state.get(listId).hasEverBeenPublished;
      const hasEverBeenPublished =
        hasBeenPublishedBefore || !lastPersistActionWasNotAPublishListAction;

      return state.set(listId, {
        ...state.get(listId),
        hasEverBeenPublished,
        hasUnpublishedChanges: lastPersistActionWasNotAPublishListAction,
        publishingState,
      });
    }

    case ContainerPersistEditActionTypes.PERSIST_PUBLISH_LIST: {
      const listId = action.editAction.payload.listId;

      return state.set(listId, {
        ...state.get(listId),
        hasUnpublishedChanges: false,
        publishingState: ListPublishingState.PUBLISH_ACTIONED,
      });
    }

    case ContainerPersistEditActionTypes.PERSIST_PUBLISH_LIST_CHECK: {
      const { listId, reviewUrl } = action.payload;

      return state.set(listId, {
        ...state.get(listId),
        hasUnpublishedChanges: false,
        publishingState: ListPublishingState.PUBLISH_ACTION_PERSISTED,
        activeReviewUrl: reviewUrl,
      });
    }

    case RequestReviewActionTypes.REQUEST_REVIEW_SEND_REQUEST_SUCCEEDED: {
      const { listId, review } = action.payload;
      const activeReviewUrl = review.review_url;

      return state.set(listId, {
        ...state.get(listId),
        activeReviewUrl,
      });
    }

    case NodesEditorActionTypes.NODES_EDITOR_SAVE_NODES_SUCCEEDED: {
      const { listId, hierarchyAndStudentNumbers } = action.payload;
      const totalStudentNumbers = getTotalStudentNumbers(
        hierarchyAndStudentNumbers,
      );

      const nodes = (hierarchyAndStudentNumbers.nodesAndOverrides || []).map(
        nodeAndOverrides => nodeAndOverrides.node,
      );

      return state.set(listId, {
        ...state.get(listId),
        nodes,
        totalStudentNumbers,
      });
    }

    case ListOwnersEditorActionTypes.LIST_OWNERS_EDITOR_SAVE_SUCCEEDED: {
      const { listId, ownersWithPublisherFlags } = action.payload;
      const owners = ownersWithPublisherFlags.map(
        ownersWithPublisherFlag => ownersWithPublisherFlag.owner,
      );

      return state.set(listId, {
        ...state.get(listId),
        owners,
      });
    }

    case ListDetailsEditorActionTypes.LIST_DETAILS_EDITOR_SAVE_SUCCEEDED: {
      const { listId, listDetails } = action.payload;
      const {
        title,
        description,
        internalNote,
        license,
        timePeriod,
        listAccess,
      } = listDetails;

      return state.set(listId, {
        ...state.get(listId),
        title,
        description,
        internalNote,
        license,
        period: timePeriod,
        listAccess,
      });
    }

    default: {
      return state;
    }
  }
}
